import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import isequal from 'lodash.isequal'

import { FileMeta } from '@pollination-solutions/pollination-sdk'
import { ChevronRight, CloudPlus, FolderFill } from 'react-bootstrap-icons'
import { useCombobox, UseComboboxState, UseComboboxStateChangeOptions } from 'downshift'

import { useArtifacts } from '../hooks'
import { Button, ComboBox } from '../atoms'

import { getFileIcon } from './getFileTypeIcon'

import { SelectCloudArtifactsProps } from './SelectCloudArtifacts.types'
import { useDebounce } from '../hooks/useDebounce'

const longDebounce = useDebounce(500)
const shortDebounce = useDebounce(200)

export const SelectCloudArtifacts: React.FC<SelectCloudArtifactsProps> = ({
  projectOwner,
  projectName,
  jobId,
  runId,
  value,
  hidden,
  fileNameMatch='.*',
  onChange,
  client,
  enableFolderSelection=false
}) => {
  
  const comboBoxRef = useRef(null)

  const valueRef = useRef<FileMeta>()
  
  const { listArtifacts } = useArtifacts(projectOwner, projectName, client)

  const [selArtifact, setSelArtifact] = useState<FileMeta>()
  const [loading, setLoading] = useState(false)
  const [artifacts, setArtifacts] = useState<{folders: FileMeta[], files: FileMeta[]}>({ folders: [], files: [] })

  const [path, setPath] = useState<string[]>([])
  const [folder, setFolder] = useState<FileMeta>()

  // Only at bootstrap
  const initialPath = useMemo(() => {
    if (!value || !value.key) return []
    const re = /(?:\.([^.]+))?$/
    const ext = re.exec(value.key)
    if (ext && ext[1]) return [value.key.split('/').slice(0, -1).join('/')]
    else return [value.key]
  }, [])

  useEffect(() => {
    setPath(initialPath ?? [])
  }, [initialPath])

  const handleClear = useCallback(() => {
    setPath(initialPath ?? [])
    setFolder(undefined)
  }, [])

  // resetting query
  useEffect(() => {
    // deselect item
    if(comboBoxRef.current) {
      comboBoxRef.current.selectItem(null)
    }

    setArtifacts({ folders: [], files: [] })
  }, [projectOwner, projectName])

  longDebounce(
    () => {
      if (!client || !client.projects) return

      setLoading(true)

      // Use path by navigator and initial path just first time
      listArtifacts(path ?? initialPath, jobId, runId)
        .then((facts) => {
  
          const folders = facts.filter(f => f.file_type === 'folder')
          const files = facts.filter(f => f.file_type === 'file')
      
          setArtifacts({ folders, files })
        })
        .catch(() => setLoading(false))
        .finally(() => setLoading(false))
    },
    [runId, jobId, listArtifacts, path]
  )

  useEffect(() => {
    if (!selArtifact || selArtifact.file_type !== 'folder') return

    setPath(path => {
      const newPath = [`${path}/${selArtifact.file_name}`]
      return newPath
    })

  }, [selArtifact])

  shortDebounce(
    () => {
      if (isequal(valueRef.current, selArtifact) ||
      (!valueRef.current && !selArtifact)
      ) {
        return
      }

      // if selection is a folder instead of an artifact
      if (selArtifact && selArtifact.file_type === 'folder' && selArtifact.key !== folder?.key) return

      comboBoxRef.current.selectItem( selArtifact )
      valueRef.current = selArtifact

      // don't call onChange for undefined values
      onChange && onChange(selArtifact)
    },
    [onChange, selArtifact]
  )
  
  const stateReducer = useCallback((
    state: UseComboboxState<{ id: string; name: string; }>, 
    actionAndChanges: UseComboboxStateChangeOptions<{ id: string; name: string; }>
  ) => {

    const { type, changes } = actionAndChanges

    switch (type) {
      case useCombobox.stateChangeTypes.FunctionCloseMenu:
      case useCombobox.stateChangeTypes.InputBlur :
        // @ts-ignore
        if (changes.selectedItem && changes.selectedItem.file_type === 'folder') {
          // It will rerun a couple of time
          // if folder is clicked from select it closes the navigation
          //@ts-ignore
          return { ...changes, isOpen: !(folder && folder.key === changes.selectedItem.key) }
        } else return changes
      default:
        return changes
    }
    
  }, [folder])

  const fileMatchRegex = useMemo(() => {
    return new RegExp(fileNameMatch)
  }, [fileNameMatch])

  const items = useMemo(() => 

    [...artifacts.folders, ...artifacts.files]
      .map(a => {
        
        const match =  a.file_name.match(fileMatchRegex)
        const disabled = a.file_type === 'folder' || (match && match.length > 0) ? false : true

        return ({ ...a, name: a.file_name, id: a.key, disabled })
      })
      .sort((a, b) => a.file_type === 'folder' ? -1 : 1)

  , [artifacts.files, artifacts.folders, fileMatchRegex])

  return (
    <ComboBox
      items={items}
      ref={comboBoxRef}
      renderItem={(item: any) => {
        const tokens = item.file_name.split('.')
        const extension = tokens[tokens.length - 1]

        const icon = item.file_type === 'file' ? getFileIcon(extension, item.disabled) : <FolderFill/>

        return (
          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center'
            }}
          >
          {icon}
          {item.name}
          {enableFolderSelection && item.file_type === 'folder' && 
            <Button key={item.file_name} style={{ 
              marginLeft: 'auto',
              height: '24px',
              fontSize: '14px' }}
              onClick={(e: any) => {
                setFolder(item)
              }} >Select</Button>}
          </div>
        )
      }}
      setSelected={setSelArtifact}
      onClear={handleClear}
      loading={loading}
      disabled={!projectName || !projectOwner }
      hidden={hidden}
      icon={<CloudPlus />}
      header={
        path.length > 0 &&
        <div
          key="header"
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            color: 'var(--slate11)',
            padding: '6px 0px',
            gap: 4,
            alignItems: 'center',
            paddingLeft: 4,
            // zIndex: 1000,
          }}
        >
          <button
            key={'root'}
            style={{
              display: 'flex',
              border:'none',
              backgroundColor: 'none',
              borderRadius: 999,
              padding: '2px 8px',
              cursor: 'pointer',
              alignItems: 'center',
              gap: 8,
            }}
            onClick={handleClear}
          >
            <FolderFill />
            Root
          </button>
          <ChevronRight/>
          {path[0].split('/').slice(1).map((s, i, array) => (
            <>
              <button
                key={s}
                style={{
                  border:'none',
                  backgroundColor: 'none',
                  borderRadius: 999,
                  padding: '2px 8px',
                  cursor: 'pointer',
                  maxWidth: 50,
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
                onClick={(e) => {
              
                  const newPath = '/' + array.slice(0,i + 1).join('/')
                  const newPathArray = newPath === '' ? [] : [newPath]
                  setPath(newPathArray)
                  
                }}
              >
                {s}
              </button>
              {i !== array.length -1 && <ChevronRight/>}
            </>
          ))}
        </div>
      }
      footer={(
        <div
          key="footer"
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            color: 'var(--slate11)',
            padding: '6px 0px',
          }}
        >
          {`Showing ${artifacts.folders.length ?? 0} folder${artifacts.folders.length === 1 ? '' : 's'}, and ${artifacts.files.length ?? 0} file${artifacts.files.length === 1 ? '' : 's'}.`}
        </div>
      )}
      maxHeight={340}
      stateReducer={stateReducer}
    />
  )
}

export default SelectCloudArtifacts 